import * as React from 'react';
import { useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

import * as API from '../../api';
import { RootState } from '@/reducers/reducers';
import { VCSDirectPushBranchTarget, VCSDirectPushCheckResult, VCSDirectPushPrerequisite } from '../../types/automation';
import { antGold, antGreen, antGrey, antRed, runwayColors, titleStyle } from '../styles/globalStyles';

interface Props {
    orgId: string;
    appId: string;
    branchTarget: VCSDirectPushBranchTarget | null;
    disabledReason?: string;
    createsMergeCommit: boolean;
    canWaitForRequiredStatusChecks: boolean;
}

export default function VCSDirectPushCheck({
    orgId,
    appId,
    branchTarget,
    disabledReason,
    createsMergeCommit,
    canWaitForRequiredStatusChecks,
}: Props): JSX.Element {
    const authCredentials = useSelector((state: RootState) => state.auth.authCredentials);

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasRun, setHasRun] = React.useState(false);
    const [result, setResult] = React.useState<VCSDirectPushCheckResult | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [unavailableReason, setUnavailableReason] = React.useState<string | null>(null);

    const clearResult = React.useCallback(() => {
        setResult(null);
        setErrorMessage(null);
        setUnavailableReason(null);
        setHasRun(false);
    }, []);

    React.useEffect(() => {
        clearResult();
    }, [appId, branchTarget, canWaitForRequiredStatusChecks, clearResult, createsMergeCommit, orgId]);

    const runCheck = () => {
        if (!branchTarget || !authCredentials) {
            return;
        }

        clearResult();
        setIsLoading(true);

        API.checkVCSDirectPush(
            { orgId, appId, branchTarget, createsMergeCommit, canWaitForRequiredStatusChecks },
            authCredentials,
        )
            .then((response) => {
                if (!response.available || !response.result) {
                    setUnavailableReason(response.unavailableReason ?? 'Runway cannot check this repository.');
                    return;
                }
                setResult(response.result);
                return;
            })
            .catch((e) => setErrorMessage(e.message))
            .finally(() => {
                setHasRun(true);
                setIsLoading(false);
                setIsModalOpen(true);
            });
    };

    const isDisabled = !!disabledReason || !branchTarget || !authCredentials;
    const button = (
        <Button onClick={runCheck} loading={isLoading} disabled={isDisabled}>
            {isLoading ?
                'Testing'
            : hasRun ?
                'Test again'
            :   'Run a test to check prerequisites'}
        </Button>
    );

    return (
        <Space size={12} align="center" style={{ marginBottom: 12 }}>
            {disabledReason ?
                <Tooltip title={disabledReason}>
                    <span>{button}</span>
                </Tooltip>
            :   button}

            <Summary
                isLoading={isLoading}
                hasRun={hasRun}
                result={result}
                errorMessage={errorMessage}
                unavailableReason={unavailableReason}
                onClick={() => setIsModalOpen(true)}
            />

            <VCSDirectPushCheckModal
                open={isModalOpen}
                result={result}
                errorMessage={errorMessage}
                unavailableReason={unavailableReason}
                onClose={() => setIsModalOpen(false)}
            />
        </Space>
    );
}

function Summary(props: {
    isLoading: boolean;
    hasRun: boolean;
    result: VCSDirectPushCheckResult | null;
    errorMessage: string | null;
    unavailableReason: string | null;
    onClick: () => void;
}): JSX.Element | null {
    const { isLoading, hasRun, result, errorMessage, unavailableReason, onClick } = props;

    if (isLoading || !hasRun) {
        return null;
    }

    if (errorMessage || unavailableReason) {
        return <Text type="secondary">Couldn&apos;t check</Text>;
    }

    if (!result) {
        return null;
    }

    const passed = result.status === 'passed';
    const warning = result.status === 'warning';
    const color =
        passed ? antGreen.green6
        : warning ? antGold.gold6
        : antRed.red6;
    const label =
        passed ? 'Passed'
        : warning ? 'Needs review'
        : 'Not passed';

    return (
        <Space size={6} align="center" style={{ cursor: 'pointer' }} onClick={onClick}>
            {passed ?
                <CheckCircleOutlined style={{ color }} />
            : warning ?
                <ExclamationCircleOutlined style={{ color }} />
            :   <CloseCircleOutlined style={{ color }} />}
            <Text style={{ color }}>{label}</Text>
        </Space>
    );
}

const BRANCH_PROTECTION_DOCS_URL =
    'https://docs.runway.team/integrations/version-control/github#branch-protection-rules';

interface ModalProps {
    open: boolean;
    result: VCSDirectPushCheckResult | null;
    errorMessage: string | null;
    unavailableReason: string | null;
    onClose: () => void;
}

function VCSDirectPushCheckModal(props: ModalProps): JSX.Element {
    const { open, result, errorMessage, unavailableReason, onClose } = props;

    return (
        <Modal
            open={open}
            title="Bypass pull requests prerequisites"
            onCancel={onClose}
            onOk={onClose}
            cancelButtonProps={{ style: { display: 'none' } }}
            okText="Done"
            width={640}
        >
            {unavailableReason ?
                <Alert type="info" showIcon message={unavailableReason} />
            :   <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <RulesetsSection result={result} errorMessage={errorMessage} />
                    <BranchProtectionSection />
                </Space>
            }
        </Modal>
    );
}

function RulesetsSection(props: { result: VCSDirectPushCheckResult | null; errorMessage: string | null }): JSX.Element {
    const { result, errorMessage } = props;

    return (
        <div>
            <SectionTitle title="Rulesets" />

            {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

            {!errorMessage && result && (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Alert type={alertType(result)} showIcon message={verdictMessage(result)} />

                    {groupPrerequisitesByPolicy(result.prerequisites).map((group) => (
                        <PolicyCard key={group.policyLabel} group={group} />
                    ))}

                    <CheckedCount prerequisites={result.prerequisites} branch={result.branch} />
                </Space>
            )}
        </div>
    );
}

function alertType(result: VCSDirectPushCheckResult): 'success' | 'warning' | 'error' {
    if (result.status === 'blocked') {
        return 'error';
    }
    return result.status === 'warning' ? 'warning' : 'success';
}

function verdictMessage(result: VCSDirectPushCheckResult): string {
    const branch = `your ${result.branchLabel}`;

    if (result.status === 'passed') {
        return `No blocking rulesets found for ${branch}.`;
    }

    if (result.status === 'warning') {
        return `No definite blocker found for ${branch}, but some prerequisites need review.`;
    }

    const blockingPolicies = new Set(
        result.prerequisites
            .filter((prerequisite) => prerequisite.status === 'blocked' && prerequisite.policy)
            .map((prerequisite) => prerequisite.policy?.label),
    );

    // Nothing to advise when the blocker isn't a ruleset, e.g. a missing repository permission
    if (blockingPolicies.size === 0) {
        return `Runway can't push directly to ${branch}.`;
    }

    return `Runway can't push directly to ${branch}. Add the Runway GitHub App to the bypass lists of the rulesets below.`;
}

interface PolicyGroup {
    policyLabel: string;
    policyUrl?: string;
    blocked: boolean;
    prerequisites: VCSDirectPushPrerequisite[];
}

function groupPrerequisitesByPolicy(prerequisites: VCSDirectPushPrerequisite[]): PolicyGroup[] {
    const groups: PolicyGroup[] = [];

    prerequisites
        .filter((prerequisite) => prerequisite.status !== 'passed')
        .forEach((prerequisite) => {
            const policyLabel = prerequisite.policy?.label ?? '';
            const existing = groups.find((group) => group.policyLabel === policyLabel);

            if (existing) {
                existing.prerequisites.push(prerequisite);
                existing.blocked = existing.blocked || prerequisite.status === 'blocked';
                existing.policyUrl = existing.policyUrl ?? prerequisite.policy?.url;
                return;
            }

            groups.push({
                policyLabel,
                policyUrl: prerequisite.policy?.url,
                blocked: prerequisite.status === 'blocked',
                prerequisites: [prerequisite],
            });
        });

    return groups;
}

function PolicyCard(props: { group: PolicyGroup }): JSX.Element {
    const { group } = props;

    return (
        <Space
            direction="vertical"
            size={4}
            style={{
                width: '100%',
                backgroundColor: runwayColors.white,
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 6,
                padding: 12,
            }}
        >
            {group.policyLabel && (
                <Text strong>
                    {group.blocked ? 'Blocked by' : 'Flagged by'} {group.policyLabel}
                </Text>
            )}

            <ul
                style={{
                    margin: 0,
                    paddingLeft: group.policyLabel ? 18 : 0,
                    listStyle: group.policyLabel ? undefined : 'none',
                }}
            >
                {group.prerequisites.map((prerequisite) => (
                    <li key={prerequisite.name}>
                        <Text>{prerequisite.name}</Text>
                        {prerequisite.explanation && (
                            <div>
                                <Text type="secondary">{prerequisite.explanation}</Text>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {group.policyUrl && (
                <a href={group.policyUrl} target="_blank" rel="noreferrer">
                    View ruleset
                </a>
            )}
        </Space>
    );
}

function CheckedCount({
    prerequisites,
    branch,
}: {
    prerequisites: VCSDirectPushPrerequisite[];
    branch: string;
}): JSX.Element | null {
    const uniqueRuleNames = [...new Set(prerequisites.map((prerequisite) => prerequisite.name))];

    if (uniqueRuleNames.length === 0) {
        return null;
    }

    return (
        <Text type="secondary">
            {uniqueRuleNames.length} prerequisites checked against <Text code>{branch}</Text>
            <Tooltip
                title={
                    <>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Prerequisites checked</div>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {uniqueRuleNames.map((name) => (
                                <li key={name}>{name}</li>
                            ))}
                        </ul>
                    </>
                }
            >
                <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />
            </Tooltip>
        </Text>
    );
}

function BranchProtectionSection(): JSX.Element {
    return (
        <div>
            <SectionTitle title="Branch protection rules (legacy)" />
            <Alert
                showIcon
                type="info"
                // Ensures we match the border from other banners since they'll appear together
                style={{ border: `1px solid ${runwayColors.primary}50` }}
                message={
                    <>
                        {`Legacy branch protection rules require admin permissions, which Runway doesn't have access to. If you have any legacy rules set, you must allow the Runway app to bypass them. `}
                        <a href={BRANCH_PROTECTION_DOCS_URL} target="_blank" rel="noreferrer">
                            Learn more
                        </a>
                    </>
                }
            />
        </div>
    );
}

function SectionTitle(props: { title: string }): JSX.Element {
    return (
        <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 14, marginBottom: 16, display: 'block' }}>
            {props.title}
        </Text>
    );
}
